"use client";

import { useEffect, useRef } from "react";
import { useGetUserInfo } from "@/services/queries/useGetUserInfo";
import { useSubscription } from "@/hooks/useSubscription";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import {
	initClarity,
	claritySetIdentity,
	claritySetTag,
} from "@/lib/clarity";

/**
 * ClarityProvider — irmão do PostHogProvider.
 *
 * Responsabilidades:
 *   1. Injetar o script do Clarity uma vez no mount.
 *   2. Identificar o usuário quando os dados chegam (idempotente).
 *   3. Setar tags globais (plan, role, ocupacao, subscription_status,
 *      days_remaining) que segmentam toda análise no dashboard.
 *
 * Não precisa traquear pageviews — o Clarity faz nativo.
 */
export function ClarityProvider({ children }: { children: React.ReactNode }) {
	const { data: user } = useGetUserInfo();
	const { plan, isPremium, isTrial } = useSubscription();
	const { phase, daysRemaining } = useTrialStatus();
	const identifiedRef = useRef<string | null>(null);

	useEffect(() => {
		initClarity();
	}, []);

	useEffect(() => {
		if (!user?.id) return;
		const identityKey = `${user.id}-${user.email}-${plan}-${phase}`;
		if (identifiedRef.current === identityKey) return;

		// Identidade — decisão do owner (Paulo): email como friendlyName no
		// painel Clarity, pra facilitar busca/filtro por usuário específico.
		// customId continua sendo o ID interno (estável, idempotente).
		claritySetIdentity({
			customId: user.id,
			friendlyName: user.email ?? user.name ?? `#${user.id}`,
		});

		// Tags globais — segmentação base
		if (user.email) claritySetTag("email", user.email);
		if (user.name) claritySetTag("name", user.name);
		claritySetTag("user_id", String(user.id));
		claritySetTag("plan", plan ?? "unknown");
		claritySetTag("is_premium", isPremium ? "true" : "false");
		claritySetTag("is_trial", isTrial ? "true" : "false");
		claritySetTag("subscription_status", phase ?? "unknown");
		if (typeof daysRemaining === "number") {
			claritySetTag("days_remaining", String(daysRemaining));
		}
		if (user.ocupacao) claritySetTag("ocupacao", user.ocupacao);

		identifiedRef.current = identityKey;
	}, [
		user?.id,
		user?.email,
		user?.name,
		user?.ocupacao,
		plan,
		isPremium,
		isTrial,
		phase,
		daysRemaining,
	]);

	return <>{children}</>;
}
